import React, { useContext, useEffect, useRef, useState, Dispatch } from "react";
import FeatureService from 'mapbox-gl-arcgis-featureserver';
import { AppContext } from "context";
import { getEsriVectorSourceStyle, addEsriLayersFromVectorSourceStyle } from "utils/MappingUtil";
import Countries from "components/map/Layers/Countries";
import StudyPins from "components/map/Layers/StudyPins";
import { MapResources, DefaultMapboxMapOptions, Expressions, MapSymbology } from 'components/map/MapConfig'
import { CountriesMapConfig, StudyPinsMapConfig } from "types";
// @ts-ignore
// eslint-disable-next-line
import mapboxgl, { Style } from '!mapbox-gl';
import "components/map/MapboxMap.scss";
import "mapbox-gl/dist/mapbox-gl.css";

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY as string;

function mapOnLoad(map: mapboxgl.Map, dispatch: Dispatch<any>, baseMapStyle: any) {
  const layerId = 'Countries';

  map.addLayer({
    id: layerId,
    type: 'fill',
    source: 'Countries',
    paint: Expressions.CountriesPaint as any,
    layout: Expressions.CountriesLayout as any,
    promoteId: 'ISO_3_CODE',
    filter: ['!=', 'OBJECTID', 694]
  })
  map.moveLayer("Countries", layerId); // HACK for now, moves countries layer behind border once loaded.
  map.on("mouseenter", layerId, function (e: any) {
    dispatch({ type: 'HIDE_COUNTRY_HOVER' })
  });
  map.on("mouseleave", layerId, function (e: any) {
    dispatch({ type: 'SHOW_COUNTRY_HOVER' })
  });

  const { id: _, ...jammuKashmirLayer } = baseMapStyle.layers.find((layer: any) => layer.id === 'DISPUTED BORDERS AND AREAS/DISPUTED_AREAS_BB/Not applicable/1');

  map.addLayer({
    ...jammuKashmirLayer,
    id: 'jammu-kashmir-layer'
  });

  map.addSource('abyei-polygon-source', {
    type: 'geojson',
    data: 'https://raw.githubusercontent.com/serotracker/iit-backend-v2/refs/heads/main/geojson/abyei-polygon.geojson'
  });

  map.addLayer({
    id: 'abyei-polygon-layer',
    source: 'abyei-polygon-source',
    type: 'fill',
    paint: {
      'fill-color': MapSymbology.CountryFeature.Disputed.Color,
      'fill-opacity': MapSymbology.CountryFeature.Disputed.Opacity
    },
  });

  map.addSource('abyei-line-source', {
    type: 'geojson',
    data: 'https://raw.githubusercontent.com/serotracker/iit-backend-v2/refs/heads/main/geojson/abyei-line.geojson'
  });

  map.addLayer({
    id: 'abyei-line-layer',
    source: 'abyei-line-source',
    type: 'line',
    paint: {
      'line-color': '#AEAEAE',
      'line-dasharray': [3, 3],
      'line-width': 1
    }
  });

  map.addSource('aksai-chin-bandaid-source', {
    type: 'geojson',
    data: 'https://raw.githubusercontent.com/serotracker/iit-backend-v2/refs/heads/main/geojson/aksai-chin-bandaid-polygon.geojson'
  });

  map.addLayer({
    id: 'aksai-chin-bandaid-layer',
    source: 'aksai-chin-bandaid-source',
    type: 'fill',
    paint: {
      'fill-color': MapSymbology.CountryFeature.Disputed.Color,
      'fill-opacity': 1
    }
  });

  map.addSource('aksai-chin-polygon-source', {
    type: 'geojson',
    data: 'https://raw.githubusercontent.com/serotracker/iit-backend-v2/refs/heads/main/geojson/aksai-chin-polygon.geojson'
  });

  map.addLayer({
    id: 'aksai-chin-polygon-background-layer',
    source: 'aksai-chin-polygon-source',
    type: 'fill',
    paint: {
      'fill-color': MapSymbology.CountryFeature.Default.Color,
      'fill-opacity': 1
    }
  });

  map.addLayer({
    id: 'aksai-chin-polygon-layer',
    source: 'aksai-chin-polygon-source',
    type: 'fill',
    paint: {
      'fill-color': [
        'match',
        ['get', 'zone'],
        "zone-one",
        MapSymbology.CountryFeature.Default.Color,
        "zone-two",
        MapSymbology.CountryFeature.Disputed.Color,
        MapSymbology.CountryFeature.Default.Color,
      ],
      'fill-opacity': [
        'match',
        ['get', 'zone'],
        "zone-one",
        MapSymbology.CountryFeature.Default.Opacity,
        "zone-two",
        MapSymbology.CountryFeature.Disputed.Opacity,
        MapSymbology.CountryFeature.Default.Opacity,
      ]
    }
  });

  map.addSource('aksai-chin-border-line-source', {
    type: 'geojson',
    data: 'https://raw.githubusercontent.com/serotracker/iit-backend-v2/refs/heads/main/geojson/aksai-chin-border-line.geojson'
  });

  map.addLayer({
    id: 'aksai-chin-border-line-layer',
    source: 'aksai-chin-border-line-source',
    type: 'line',
    paint: {
      'line-color': '#AEAEAE',
      'line-width': 1
    }
  });

  const lineLayers = baseMapStyle.layers
    .filter((layer: any) => layer.type === 'line')
    .map((layer: any) => ({
      ...layer,
      paint: {
        ...layer.paint,
        "line-color": "#A9A9A9"
      }
    }));
  
  for (const lineLayer of lineLayers) {
    map.addLayer({
      ...lineLayer,
      id: `${lineLayer.id}-2`
    });
  }
}

interface MapboxMapProps {
  mapConfig?: any
  countriesConfig: CountriesMapConfig,
  studyPinsConfig: StudyPinsMapConfig
}

const adjustMapStyle = (mapStyle: any): any => {
  return {
    ...mapStyle,
    layers: mapStyle.layers.map((layer: any) => {
      if(layer.id === 'GLOBAL/1') {
        return {
          ...layer,
          paint: (layer.paint ? {
            ...layer.paint,
            "fill-color": "#FFFFFF"
          } : layer.paint)
        }
      }

      if(layer.type === 'line') {
        return {
          ...layer,
          paint: {
            ...layer.paint,
            "line-color": "#A9A9A9"
          }
        }
      }

      return layer
    })
  }
}

const MapboxMap = ( {mapConfig, countriesConfig, studyPinsConfig}: MapboxMapProps ): any => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [state, dispatch] = useContext(AppContext);
  const [map, setMap] = useState<mapboxgl.Map | undefined>(undefined);

  // Creates map, only runs once
  useEffect(() => {
    (async () => {
      const baseMapStyle = await getEsriVectorSourceStyle(MapResources.WHO_BASEMAP_BB);
      const adjustedBaseMapStyle = adjustMapStyle(baseMapStyle);

      const mergedOptions = { // Merges options together to configure map
        ...{
          container: mapContainerRef.current,
          style: adjustedBaseMapStyle,
        },
        ...DefaultMapboxMapOptions,
        ...mapConfig,
      };

      //base map style has 31 layers
      //Countries layer is added on top later.
      const m: mapboxgl.Map = new mapboxgl.Map(mergedOptions).addControl(new mapboxgl.NavigationControl());
      m.on("load", () => {
        const featureService = new FeatureService(
          "Countries",
          m,
          {
            url: MapResources.WHO_COUNTRY_VECTORTILES,
          }
        )
        mapOnLoad(m, dispatch, baseMapStyle);
        setMap(m);
      });
    })();

    return () => map?.remove();
  }, []);

  // Adds country data to map and binds pin behaviour with map popups
  Countries(map, countriesConfig);
  // Adds pins to map and binds pin behaviour with map popups
  StudyPins(map, studyPinsConfig);

  return (
    //@ts-ignore
    <div className="w-100 h-100 overflow-hidden" ref={(el) => (mapContainerRef.current = el)}/>
  );
};

export default MapboxMap;
